import { useEffect, useState } from "react";
import api from "../lib/typedApi";
import { PageHeader, NBCard, NBInput, NBSelect, NBButton, NBTextarea, EmptyState, Avatar } from "../components/UI";
import { toast } from "sonner";
import { Plus, Trash2, X, BookOpen } from "lucide-react";

const MOODS = [
  { id: "happy", label: "😊 Happy", color: "#E8B96B" },
  { id: "proud", label: "🏆 Proud", color: "#E9B5A0" },
  { id: "grateful", label: "🙏 Grateful", color: "#B8CFA9" },
  { id: "milestone", label: "🎯 Milestone", color: "#A8BFD1" },
  { id: "funny", label: "😂 Funny", color: "#E8B96B" },
];

export default function Journal() {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", photo_url: "", mood: "happy" });

  const load = async () => {
    const { data } = await api.get("/api/journal");
    setEntries(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/journal", form);
      toast.success("Entry posted!");
      setOpen(false);
      setForm({ title: "", content: "", photo_url: "", mood: "happy" });
      load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete entry?")) return;
    await api.delete(`/api/journal/${id}`);
    load();
  };

  const moodFor = (id) => MOODS.find((m) => m.id === id) || MOODS[0];

  return (
    <div data-testid="journal-page">
      <PageHeader
        title="Family Journal"
        subtitle="Memories, milestones, and the small everyday moments worth remembering."
        color="#E9B5A0"
        action={<NBButton testId="add-journal-btn" color="#E9B5A0" onClick={() => setOpen(true)}><Plus strokeWidth={3} className="w-4 h-4 inline mr-1" />New Entry</NBButton>}
      />

      {entries.length === 0 ? (
        <EmptyState title="The first page is blank" subtitle="Capture a memory, a funny quote, a milestone — anything." action={<NBButton color="#E9B5A0" onClick={() => setOpen(true)}>Write the first entry</NBButton>} />
      ) : (
        <div className="space-y-6">
          {entries.map((e) => {
            const mood = moodFor(e.mood);
            return (
              <NBCard key={e.id} color={mood.color} data-testid={`journal-${e.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <Avatar name={e.author_name} color="white" size={40} />
                    <div>
                      <p className="font-bold text-sm">{e.author_name}</p>
                      <p className="text-xs text-neutral-700">{new Date(e.created_at).toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="nb-badge bg-white">{mood.label}</span>
                    <button onClick={() => remove(e.id)} className="nb-btn bg-white p-1.5">
                      <Trash2 strokeWidth={2.5} className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <h3 className="text-2xl font-black mt-4" style={{ fontFamily: "var(--hh-display)" }}>{e.title}</h3>
                {e.photo_url && (
                  <div className="mt-3 rounded-lg overflow-hidden border-2 border-black">
                    <img src={e.photo_url} alt={e.title} className="w-full max-h-96 object-cover" />
                  </div>
                )}
                <p className="text-sm mt-3 whitespace-pre-wrap leading-relaxed">{e.content}</p>
              </NBCard>
            );
          })}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="nb-card max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(ev) => ev.stopPropagation()} style={{ boxShadow: "8px 8px 0 0 rgba(0,0,0,1)" }}>
            <div className="flex justify-between mb-4">
              <h2 className="text-2xl font-black" style={{ fontFamily: "var(--hh-display)" }}>New Entry</h2>
              <button onClick={() => setOpen(false)} className="nb-btn bg-[#E9B5A0] p-1.5"><X strokeWidth={3} className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <NBInput testId="journal-title" required placeholder="Title (e.g. 'Lily's first soccer goal!')" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <NBSelect testId="journal-mood" value={form.mood} onChange={(e) => setForm({ ...form, mood: e.target.value })}>
                {MOODS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </NBSelect>
              <NBTextarea testId="journal-content" required placeholder="Tell the story..." value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="min-h-[150px]" />
              <NBInput testId="journal-photo" placeholder="Photo URL (optional)" value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} />
              <NBButton testId="submit-journal" type="submit" color="#E9B5A0" className="w-full">Post Entry</NBButton>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
